'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CourseCardProps {
  course: {
    id: string;
    title: string;
    instructor: string;
    instructorImage: string;
    instructorImageAlt: string;
    duration: string;
    level: string;
    rating: number;
    studentsEnrolled: number;
    price: number;
    thumbnail: string;
    thumbnailAlt: string;
    category: string;
    format: string;
    startDate: string;
    description: string;
  };
  viewMode: 'grid' | 'list';
}

const CourseCard = ({ course, viewMode }: CourseCardProps) => {
  if (viewMode === 'list') {
    return (
      <Link href={`/individual-course-page?id=${course.id}`} className="block">
        <div className="bg-card rounded-lg shadow-card hover:shadow-brand transition-shadow duration-300 overflow-hidden border border-border">
          <div className="flex flex-col md:flex-row">
            <div className="md:w-80 h-48 md:h-auto overflow-hidden flex-shrink-0">
              <AppImage
                src={course.thumbnail}
                alt={course.thumbnailAlt}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="flex-1 p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-brand-secondary text-brand-primary text-xs font-semibold rounded-full">
                      {course.category}
                    </span>
                    <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
                      {course.level}
                    </span>
                  </div>
                  <h3 className="text-xl font-headline font-bold text-foreground mb-2 hover:text-primary transition-colors">
                    {course.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {course.description}
                  </p>
                </div>
                <div className="text-right ml-4">
                  <div className="text-2xl font-headline font-bold text-brand-primary">
                    ₹{course.price.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                    src={course.instructorImage}
                    alt={course.instructorImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-sm font-medium text-foreground">{course.instructor}</span>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Icon name="StarIcon" size={16} variant="solid" className="text-warning" />
                  <span className="font-semibold text-foreground">{course.rating}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="UserGroupIcon" size={16} variant="outline" />
                  <span>{course.studentsEnrolled.toLocaleString()} students</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="ClockIcon" size={16} variant="outline" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="VideoCameraIcon" size={16} variant="outline" />
                  <span>{course.format}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="CalendarIcon" size={16} variant="outline" />
                  <span>Starts {course.startDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link href={`/individual-course-page?id=${course.id}`} className="block">
      <div className="bg-card rounded-lg shadow-card hover:shadow-brand transition-shadow duration-300 overflow-hidden border border-border h-full flex flex-col">
        <div className="h-48 overflow-hidden">
          <AppImage
            src={course.thumbnail}
            alt={course.thumbnailAlt}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-brand-secondary text-brand-primary text-xs font-semibold rounded-full">
              {course.category}
            </span>
            <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
              {course.level}
            </span>
          </div>

          <h3 className="text-lg font-headline font-bold text-foreground mb-2 hover:text-primary transition-colors line-clamp-2">
            {course.title}
          </h3>

          <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1">
            {course.description}
          </p>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
              <AppImage
                src={course.instructorImage}
                alt={course.instructorImageAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-sm font-medium text-foreground">{course.instructor}</span>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-1">
              <Icon name="StarIcon" size={16} variant="solid" className="text-warning" />
              <span className="text-sm font-semibold text-foreground">{course.rating}</span>
              <span className="text-xs text-muted-foreground ml-1">
                ({course.studentsEnrolled.toLocaleString()})
              </span>
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Icon name="ClockIcon" size={16} variant="outline" />
              <span>{course.duration}</span>
            </div>
          </div>

          <div className="mt-auto pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-headline font-bold text-foreground">₹{course.price.toLocaleString('en-IN')}</span>
              <div className="text-xs text-muted-foreground">
                Starts {course.startDate}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CourseCard;